import { FC } from 'react';
import { AgentService } from 'components/AgentService';
import { useCmsMemberInfo } from '@doda/common/src/hooks/useCmsMemberInfo';
import { APPLY_TO_AGENT_SERVICE_URL } from 'pages/DodaFront/View/FeaturedJobList/constants';
import { useAgentService } from 'components/AgentService/hooks/useAgentService';
import {
  JobCountResponse,
  JobSearchResultResponse,
} from '@doda/api-client/aspida/@types';
import { JobCard } from 'components/JobCard';
import { Pager, PageTitle } from '@doda/io-react/pc';
import { useAppSelector } from 'redux/hooks';
import {
  loginStatusSelector,
  memberInfoSelector,
} from '@doda/common/src/redux/features/memberInfo';
import { FollowSearchResultsNavigation } from 'components/FollowSearchResultsNavigation';
import { InitialSideBarMasterDataType } from 'components/SearchSideBar/BasicInfo/types';
import { NoResult } from 'components/NoResult';
import { FeaturedSearchConditionQuery } from 'components/SearchSideBar/types';
import { DisplayJobCount } from 'components/DisplayJobCount';
import { CardNavigationPanel } from 'components/CardNavigationPanel';
import {
  TopicJob,
  JobListMeta,
} from 'pages/DodaFront/View/FeaturedJobList/types';
import { useAnalyticsSetup } from '@doda/common/src/hooks/useAnalytics';
import { Text } from '@doda/io-react/common';
import { DODA_SITE_URL } from '@doda/common/src/constants/urls';
import styles from './index.module.scss';
import { getFeaturedJobDetailHref } from '../../logics/getFeaturedJobDetailHref';
import { useFeaturedJobListAnalytics } from '../../hooks/useFeaturedJobListAnalytics';
import { BreadcrumbList } from '../../components/BreadcrumbList';
import { getTitle } from '../../logics/getTitle';
import { HeadTag } from '../../components/HeadTag';
import {
  FeaturedModalMasterData,
  NonModal,
  NormalModalMasterData,
} from '../../components/SearchSideBar/interface';
import { NormalSearchSideBar } from '../../components/SearchSideBar/NormalSearchSideBar';
import { FeaturedSearchSideBar } from '../../components/SearchSideBar/FeaturedSearchSideBar';
import { TopicJobContents } from '../../components/TopicJobContents';
import { usePager } from '../../hooks/usePager';

/**
 * FeaturedJobListScreenProps
 * @param topicJob 特集枠情報
 * @param jobListMeta ページングに関するmetadata
 * @param jobList: 求人一覧
 * @param isNoPic: 画像無し表示かどうか
 * @param currentPage: 現在のページ
 * @param featuredId: 特集枠ID
 * @param initialSideBarMasterData: 検索サイドバー初回metadata
 * @param initialJobCountData: 検索サイドバー求人取得APIのレスポンス
 * @param modalMasterData: 検索サイドバーモーダルmetadata
 * @param featuredSearchCondition: 特集用検索条件
 * @param validationMessages: バリデーション文
 */
export interface FeaturedJobListScreenProps {
  topicJob: TopicJob;
  jobListMeta: JobListMeta;
  jobList: JobSearchResultResponse[];
  isNoPic: boolean;
  currentPage: number;
  featuredId: string;
  initialSideBarMasterData: InitialSideBarMasterDataType;
  initialJobCountData: JobCountResponse;
  modalMasterData: NormalModalMasterData | FeaturedModalMasterData | NonModal;
  featuredSearchCondition: FeaturedSearchConditionQuery;
  validationMessages: string[];
}

export const FeaturedJobListScreen: FC<FeaturedJobListScreenProps> = ({
  topicJob,
  jobList,
  isNoPic,
  jobListMeta,
  currentPage,
  featuredId,
  initialSideBarMasterData,
  initialJobCountData,
  modalMasterData,
  featuredSearchCondition,
  validationMessages,
}) => {
  useCmsMemberInfo('pcHeader');
  const memberInfo = useAppSelector(memberInfoSelector);
  const isLogin = useAppSelector(loginStatusSelector);
  const { totalCount, resultCount, offset } = jobListMeta;

  const { shouldShowAgentService } = useAgentService(memberInfo?.wtsrfl);
  const { maxPage, createPageHref } = usePager({
    totalCount,
    currentPage,
    featuredId,
  });
  useAnalyticsSetup();
  const {
    measureOnClickApply,
    measureOnClickBookmark,
    measureOnClickViewJobDetail,
  } = useFeaturedJobListAnalytics({
    featuredId,
    totalCount,
    jobList,
    topicTitle: topicJob.title,
  });
  const hasJobList = jobList.length > 0;
  const hasError = validationMessages.length > 0;
  const titleText = getTitle({ title: topicJob.title, currentPage });

  const fromCount = offset + 1;
  const toCount = resultCount + offset;
  return (
    <>
      <HeadTag
        titleText={topicJob.title}
        featuredId={featuredId}
        currentPage={currentPage}
        fromCount={fromCount}
        toCount={toCount}
        isRobots={!hasJobList}
        totalCount={totalCount}
      />
      <BreadcrumbList
        titleText={topicJob.title}
        featuredId={featuredId}
        currentPage={currentPage}
      />
      <FollowSearchResultsNavigation
        loginStatus={isLogin}
        totalCount={totalCount}
        resultCount={resultCount}
        offset={offset}
      />
      <div className={styles.featuredJobList__main}>
        <div className={styles.featuredJobList__sidebar}>
          {modalMasterData.type === 'normal' && (
            <NormalSearchSideBar
              initialJobCountData={initialJobCountData}
              initialSideBarMasterData={initialSideBarMasterData}
              modalMasterData={modalMasterData.data}
              featuredSearchCondition={featuredSearchCondition}
            />
          )}
          {modalMasterData.type === 'featured' && (
            <FeaturedSearchSideBar
              jobCountData={initialJobCountData}
              initialSideBarMasterData={initialSideBarMasterData}
              modalMasterData={modalMasterData.data}
              featuredSearchCondition={featuredSearchCondition}
            />
          )}
        </div>
        <div>
          {titleText && <PageTitle>{titleText}</PageTitle>}
          {hasError ? (
            <ul className={styles.featuredJobList__errorMessages}>
              {validationMessages.map((message) => (
                <Text as="li" size="10" key={message}>
                  ・{message}
                </Text>
              ))}
            </ul>
          ) : (
            <>
              <TopicJobContents topicJob={topicJob} />
              <div className={styles.featuredJobList__navigationPanelArea}>
                <DisplayJobCount
                  totalCount={totalCount}
                  resultCount={resultCount}
                  offset={offset}
                />
                <CardNavigationPanel hasNextPage={currentPage < maxPage} />
              </div>
              {hasJobList ? (
                <ul className={styles.featuredJobList__jobCards}>
                  {jobList.map((job) => {
                    const jid = job.jid ?? '';
                    const destinationURL = getFeaturedJobDetailHref({
                      jid,
                      isRead: job.isRead ?? false,
                      featuredId,
                    });

                    return (
                      <li key={job.jid}>
                        <JobCard
                          job={job}
                          isNoPic={isNoPic}
                          destinationURL={destinationURL}
                          onApplyDirect={measureOnClickApply}
                          onBookmark={measureOnClickBookmark(jid)}
                          onNavigateTitle={measureOnClickViewJobDetail}
                          onNavigateFooterButton={measureOnClickViewJobDetail}
                        />
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className={styles.featuredJobList__noResult}>
                  <NoResult href={DODA_SITE_URL.jobSearchTop} />
                </div>
              )}
              {hasJobList && (
                <div className={styles.featuredJobList__pager}>
                  <Pager
                    maxPage={maxPage}
                    currentPage={currentPage}
                    createPageHref={createPageHref}
                  />
                </div>
              )}
              {shouldShowAgentService && (
                <div className={styles.featuredJobList__agentService}>
                  <AgentService href={APPLY_TO_AGENT_SERVICE_URL} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
